import { AdminAction } from '@/app/models/AdminAction';
import {
  DASHBOARD_RESOURCES,
  PICKLIST_RESOURCES,
  PURCHASE_INVOICE_READ_RESOURCES,
  WAREHOUSE_TASK_RESOURCES,
} from '@/app/casl/permissionGroups';
import { systemPreferences } from '@/app/config/SettingsOptions';
import { MenuBadgeCountKey } from '@/app/config/menuBadgeCountKey';
import {
  CHECKLIST_MOCK_ENABLED,
  SOCIAL_NETWORK_MOCK_ENABLED,
  TICKETS_MOCK_ENABLED,
  TRAININGS_MOCK_ENABLED,
} from '@/app/consts/featureFlags';
import {
  ABOUT_PAGE_URL,
  CHECKLISTS_PAGE_URL,
  ACCOUNTANT_ACCESS_MANAGEMENT_PAGE_URL,
  ACCOUNTANT_DASHBOARD_PAGE_URL,
  ACCOUNTANT_INVITE_MANAGEMENT_PAGE_URL,
  ACCOUNTANT_SPACE_PAGE_URL,
  ACCOUNTS_PAYABLE_PAGE_URL,
  ACCOUNTS_RECEIVABLE_PAGE_URL,
  ANNOUNCEMENTS_PAGE_URL,
  CALENDAR_PAGE_URL,
  CASH_BANK_PAGE_URL,
  CATEGORY_PAGE_URL,
  CLIENT_FEEDBACK_PAGE_URL,
  CLIENT_PAGE_URL,
  COMMUNITY_SETTLEMENT_PAGE_URL,
  COMPETENCE_PAGE_URL,
  CONDOMINIUM_PAGE_URL,
  CONSUMER_INVOICE_LIST_PAGE_URL,
  COUPON_PAGE_URL,
  DASHBOARD_PAGE_URL,
  PROMOTION_PAGE_URL,
  EMPLOYEE_PAGE_URL,
  ENTRY_NOTE_PAGE_URL,
  FINANCIAL_REPORT_ACCOUNTS_PAYABLE_PAGE_URL,
  FINANCIAL_REPORT_ACCOUNTS_RECEIVABLE_PAGE_URL,
  FINANCIAL_REPORT_BALANCE_SHEET_PAGE_URL,
  FINANCIAL_REPORT_CASH_CATEGORY_PAGE_URL,
  FINANCIAL_REPORT_CASH_CLIENT_PAGE_URL,
  FINANCIAL_REPORT_CASH_FLOW_PAGE_URL,
  FINANCIAL_REPORT_DRE_PAGE_URL,
  FINANCIAL_REPORT_POINT_OF_SALE_RESULT_PAGE_URL,
  FINANCIAL_REPORT_PAGE_URL,
  FINANCIAL_REPORT_RECEIPTS_PAGE_URL,
  SUPPORT_PAGE_URL,
  TICKETS_PAGE_URL,
  INDEX_PAGE_URL,
  SOCIAL_NETWORK_PAGE_URL,
  TRAININGS_PAGE_URL,
  INVENTORY_OPERATIONS_PAGE_URL,
  MANAGER_DASHBOARD_PAGE_URL,
  MANAGER_SPACE_PAGE_URL,
  SUBSCRIPTION_PAGE_URL,
  NFCE_DISABLE_LIST_PAGE_URL,
  OCCURRENCE_PAGE_URL,
  OUTGOING_INVOICE_PAGE_URL,
  PICKLIST_PAGE_URL,
  PLANOGRAM_PAGE_URL,
  POINT_OF_SALE_PAGE_URL,
  PREFERENCES_PAGE_URL,
  PRICE_LIST_PAGE_URL,
  PRODUCT_PAGE_URL,
  PURCHASE_ORDER_PAGE_URL,
  PURCHASE_SUGGESTION_PAGE_URL,
  SALES_ORDER_PAGE_URL,
  SALES_REPORT_ABC_CURVE_PAGE_URL,
  SALES_REPORT_HOURLY_PAGE_URL,
  SALES_REPORT_INVOICE_CLIENT_PAGE_URL,
  SALES_REPORT_INVOICE_EVOLUTION_PAGE_URL,
  SALES_REPORT_INVOICE_OPERATION_PAGE_URL,
  SALES_REPORT_INVOICE_PRODUCT_QUERY_PAGE_URL,
  SALES_REPORT_PAGE_URL,
  SALES_REPORT_POINT_OF_SALE_CONVERSION_PAGE_URL,
  SALES_REPORT_PRODUCT_NOT_FOUND_PAGE_URL,
  SALES_REPORT_SALES_PAGE_URL,
  SALES_REPORT_RISK_ANALYSIS_PAGE_URL,
  STOCK_CONFERENCES_PAGE_URL,
  STOCK_CONTROL_PAGE_URL,
  SUPPLIER_PAGE_URL,
  SUPPLY_REPORT_ENTRY_NOTE_EVOLUTION_PAGE_URL,
  SUPPLY_REPORT_ENTRY_NOTE_OPERATION_PAGE_URL,
  SUPPLY_REPORT_ENTRY_NOTE_PRODUCT_PAGE_URL,
  SUPPLY_REPORT_ENTRY_NOTE_PRODUCT_SUPPLIER_PAGE_URL,
  SUPPLY_REPORT_ENTRY_NOTE_SUPPLIER_PAGE_URL,
  SUPPLY_REPORT_PAGE_URL,
  SUPPLY_REPORT_PRODUCT_LOSS_PAGE_URL,
  SUPPLY_REPORT_PURCHASE_ORDER_PAGE_URL,
  SUPPLY_REPORT_SHRINKAGE_PAGE_URL,
  SUPPLY_REPORT_STOCK_BALANCE_PAGE_URL,
  SUPPLY_REPORT_STOCK_COST_OF_GOODS_SOLD_PAGE_URL,
  SUPPLY_REPORT_STOCK_ENTRY_EXIT_PAGE_URL,
  SUPPLY_REPORT_STOCK_NO_CIRCULATION_PAGE_URL,
  TELEMETRY_PAGE_URL,
  TERMINAL_PAGE_URL,
} from '@/api/core/links';
import { type AppAbility } from '@/app/casl/ability-context';
import { type Resource, RESOURCES } from '@/app/casl/resources';

/**
 * Visual emphasis applied to a sidebar menu item.
 * - `tinted`: soft colored background on the whole row
 * - `accent`: brand-color icon + title text
 * - `pulse`: animated notification dot on the icon
 * - `badge`: small colored badge next to the title (set `label`)
 * - `count`: yellow circle with a number next to the title (set `label`); used by menu items with `badgeCountKey`
 */
export type SidebarHighlightVariant = 'tinted' | 'accent' | 'pulse' | 'badge' | 'count';

export interface SidebarHighlight {
  variant: SidebarHighlightVariant;
  /** Label shown for the `badge` variant (defaults to "Novo"). */
  label?: string;
}

export interface MenuItemConfig {
  title: string;
  to: string;
  description?: string;
  icon?: string;
  fontIcon?: string;
  hasBullet?: boolean;
  permissions?: Resource[];
  children?: MenuItemConfig[];
  external?: boolean;
  highlight?: SidebarHighlight;
  badgeCountKey?: MenuBadgeCountKey;
}

const homePageMenuItems: MenuItemConfig[] = SOCIAL_NETWORK_MOCK_ENABLED
  ? [
      {
        title: 'Rede Social',
        to: SOCIAL_NETWORK_PAGE_URL,
        hasBullet: true,
        description: 'Publicações, curtidas, comentários e Shorts da rede',
        highlight: { variant: 'badge', label: 'Novo' },
      },
    ]
  : [
      {
        title: 'Indice',
        to: INDEX_PAGE_URL,
        hasBullet: true,
        description: 'Página inicial',
      },
    ];

export const systemUserMenuConfig: MenuItemConfig[] = [
  // Início
  {
    title: 'Início',
    to: '/inicio',
    icon: 'category',
    fontIcon: '',
    children: [
      ...homePageMenuItems,
      {
        title: 'Comunicados',
        to: ANNOUNCEMENTS_PAGE_URL,
        hasBullet: true,
        description: 'Comunicados da equipe',
      },
      {
        title: 'Dashboard',
        to: DASHBOARD_PAGE_URL,
        hasBullet: true,
        description: 'Dashboard principal',
        permissions: DASHBOARD_RESOURCES,
      },
      {
        title: 'Agenda',
        to: CALENDAR_PAGE_URL,
        hasBullet: true,
        description: 'Calendário e agenda',
        permissions: [RESOURCES.GERENCIAR_AGENDA],
      },
      {
        title: 'Telemetria',
        to: TELEMETRY_PAGE_URL,
        hasBullet: true,
        description: 'Dados de telemetria',
        permissions: [RESOURCES.TELEMETRIA],
      },
      {
        title: 'Minha assinatura',
        to: SUBSCRIPTION_PAGE_URL,
        hasBullet: true,
        description: 'Configurações da assinatura',
        permissions: [RESOURCES.GERENCIAR_ASSINATURA],
      },
      {
        title: 'Registro de Atualizações',
        to: ABOUT_PAGE_URL,
        hasBullet: true,
        description: 'Acompanhe as novas funcionalidades e melhorias do sistema',
      },
    ],
  },

  // Cadastros
  {
    title: 'Cadastros',
    to: '/cadastros',
    icon: 'badge',
    fontIcon: '',
    children: [
      // Catálogo
      {
        title: 'Catálogo',
        to: '/cadastros/catalogo',
        icon: 'book',
        children: [
          {
            title: 'Produtos',
            to: PRODUCT_PAGE_URL,
            hasBullet: true,
            description: 'Cadastro de produtos',
            permissions: [RESOURCES.CATALOGO_PRODUTOS],
          },
          {
            title: 'Categorias',
            to: CATEGORY_PAGE_URL,
            hasBullet: true,
            description: 'Cadastro de categorias',
            permissions: [RESOURCES.CATALOGO_CATEGORIAS],
          },
          {
            title: 'Listas de Preço',
            to: PRICE_LIST_PAGE_URL,
            hasBullet: true,
            description: 'Listas de preço',
            permissions: [RESOURCES.CATALOGO_LISTAS_DE_PRECO],
          },
        ],
      },

      // Parceiros
      {
        title: 'Parceiros',
        to: '/cadastros/parceiros',
        icon: 'people',
        children: [
          {
            title: 'Clientes',
            to: CLIENT_PAGE_URL,
            hasBullet: true,
            description: 'Cadastro de clientes',
            permissions: [RESOURCES.CADASTROS_CLIENTES],
          },
          {
            title: 'Fornecedores',
            to: SUPPLIER_PAGE_URL,
            hasBullet: true,
            description: 'Cadastro de fornecedores',
            permissions: [RESOURCES.CADASTROS_FORNECEDORES],
          },
          {
            title: 'Funcionários',
            to: EMPLOYEE_PAGE_URL,
            hasBullet: true,
            description: 'Cadastro de funcionários',
            permissions: [RESOURCES.CADASTROS_FUNCIONARIOS],
          },
          {
            title: 'Condomínios',
            to: CONDOMINIUM_PAGE_URL,
            hasBullet: true,
            description: 'Cadastro de condomínios',
            permissions: [RESOURCES.CADASTROS_CONDOMINIOS],
          },
        ],
      },

      // Operação
      {
        title: 'Operação',
        to: '/cadastros/operacao',
        icon: 'shop',
        children: [
          {
            title: 'Pontos de Venda',
            to: POINT_OF_SALE_PAGE_URL,
            hasBullet: true,
            description: 'Pontos de venda',
            permissions: [RESOURCES.CADASTROS_PONTOS_DE_VENDA],
          },
          {
            title: 'Terminais',
            to: TERMINAL_PAGE_URL,
            hasBullet: true,
            description: 'Terminais',
            permissions: [RESOURCES.CADASTROS_TERMINAIS],
          },
        ],
      },
    ],
  },

  // Suprimentos
  {
    title: 'Suprimentos',
    to: '/suprimentos',
    icon: 'parcel',
    fontIcon: '',
    children: [
      {
        title: 'Controle de estoques',
        to: STOCK_CONTROL_PAGE_URL,
        hasBullet: true,
        description: 'Controle de estoque',
        permissions: [
          RESOURCES.SUPRIMENTOS_ESTOQUE,
          RESOURCES.SUPRIMENTOS_AJUSTES_DE_ESTOQUE,
          RESOURCES.SUPRIMENTOS_LOTES,
        ],
      },
      {
        title: 'Ordens de compra',
        to: PURCHASE_ORDER_PAGE_URL,
        hasBullet: true,
        description: 'Ordens de compra',
        permissions: [RESOURCES.SUPRIMENTOS_ORDEMS_DE_COMPRA],
      },
      {
        title: 'Sugestão de compra',
        to: PURCHASE_SUGGESTION_PAGE_URL,
        hasBullet: true,
        description: 'O que comprar e gerar ordens de compra',
        permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_NECESSIDADE_COMPRA],
      },
      {
        title: 'Picklists',
        to: PICKLIST_PAGE_URL,
        hasBullet: true,
        description: 'Picking',
        permissions: PICKLIST_RESOURCES,
      },
      {
        title: 'Notas de entrada',
        to: ENTRY_NOTE_PAGE_URL,
        hasBullet: true,
        description: 'Notas fiscais de entrada',
        permissions: PURCHASE_INVOICE_READ_RESOURCES,
      },
      {
        title: 'Operações de inventário',
        to: INVENTORY_OPERATIONS_PAGE_URL,
        hasBullet: true,
        description: 'Operações de inventário',
        permissions: WAREHOUSE_TASK_RESOURCES,
      },
      {
        title: 'Conferências de estoque',
        to: STOCK_CONFERENCES_PAGE_URL,
        hasBullet: true,
        description: 'Conferências de estoque',
        permissions: [RESOURCES.SUPRIMENTOS_INVENTARIO],
      },
      ...(CHECKLIST_MOCK_ENABLED
        ? [
            {
              title: 'Checklists',
              to: CHECKLISTS_PAGE_URL,
              hasBullet: true,
              description: 'Checklists da loja',
              badgeCountKey: MenuBadgeCountKey.PENDING_CHECKLISTS,
            },
          ]
        : []),
    ],
  },

  // Vendas
  {
    title: 'Vendas',
    to: '/vendas',
    icon: 'finance-calculator',
    fontIcon: '',
    children: [
      {
        title: 'Pedidos de venda',
        to: SALES_ORDER_PAGE_URL,
        hasBullet: true,
        description: 'Pedidos de venda',
        permissions: [RESOURCES.VENDAS_PEDIDOS],
      },
      {
        title: 'Planogramas',
        to: PLANOGRAM_PAGE_URL,
        hasBullet: true,
        description: 'Planogramas',
        permissions: [RESOURCES.VENDAS_PLANOGRAMAS],
      },
      {
        title: 'Cupom',
        to: COUPON_PAGE_URL,
        hasBullet: true,
        description: 'Cupons',
        permissions: [RESOURCES.VENDAS_CUPOM],
      },
      {
        title: 'Promoção',
        to: PROMOTION_PAGE_URL,
        hasBullet: true,
        description: 'Promoções',
        permissions: [RESOURCES.VENDAS_PROMOCOES],
      },
      {
        title: 'Notas de saída',
        to: OUTGOING_INVOICE_PAGE_URL,
        hasBullet: true,
        description: 'Notas fiscais de saída',
        permissions: [RESOURCES.VENDAS_NOTAS_FISCAIS],
      },
      {
        title: 'Notas fiscais consumidor',
        to: CONSUMER_INVOICE_LIST_PAGE_URL,
        hasBullet: true,
        description: 'NFC-e',
        permissions: [RESOURCES.VENDAS_NOTAS_FISCAIS_DE_CONSUMIDOR],
      },
      {
        title: 'Inutilizar NFC-e',
        to: NFCE_DISABLE_LIST_PAGE_URL,
        hasBullet: true,
        description: 'Inutilização de notas NFC-e',
        permissions: [RESOURCES.VENDAS_NOTAS_FISCAIS_DE_CONSUMIDOR],
      },
      {
        title: 'Ocorrências',
        to: OCCURRENCE_PAGE_URL,
        hasBullet: true,
        description: 'Ocorrências operacionais',
        permissions: [RESOURCES.VENDAS_OCORRENCIAS],
      },
    ],
  },

  // CRM
  {
    title: 'CRM',
    to: '/crm',
    icon: 'people',
    fontIcon: '',
    children: [
      {
        title: 'Pesquisas de Satisfação',
        to: CLIENT_FEEDBACK_PAGE_URL,
        hasBullet: true,
        description: 'Pesquisas de satisfação dos clientes',
        permissions: [RESOURCES.CRM_PESQUISAS_SATISFACAO],
      },
    ],
  },

  // Relatórios
  {
    title: 'Relatórios',
    to: '/relatorios',
    icon: 'chart-line',
    fontIcon: '',
    children: [
      // Relatórios de Vendas
      {
        title: 'Vendas',
        to: SALES_REPORT_PAGE_URL,
        description: 'O que foi vendido, quando, para quem e com que margem',
        icon: 'finance-calculator',
        children: [
          {
            title: 'Relatório de Vendas',
            to: SALES_REPORT_SALES_PAGE_URL,
            hasBullet: true,
            description: 'Vendas por produto, categoria, cliente ou PDV, com líquido, custo e margem',
            permissions: [RESOURCES.RELATORIOS_VENDAS_VENDAS],
          },
          {
            title: 'Curva ABC',
            to: SALES_REPORT_ABC_CURVE_PAGE_URL,
            hasBullet: true,
            description: 'Produtos mais vendidos (Curva de Pareto)',
            permissions: [RESOURCES.RELATORIOS_VENDAS_CURVA_ABC],
          },
          {
            title: 'Vendas por Hora',
            to: SALES_REPORT_HOURLY_PAGE_URL,
            hasBullet: true,
            description: 'Vendas agregadas por hora do dia',
            permissions: [RESOURCES.RELATORIOS_VENDAS_POR_HORA],
          },
          {
            title: 'Produtos não encontrados',
            to: SALES_REPORT_PRODUCT_NOT_FOUND_PAGE_URL,
            hasBullet: true,
            description: 'Códigos de barras lidos nos PDVs sem produto cadastrado',
            permissions: [RESOURCES.RELATORIOS_VENDAS_PRODUTOS_NAO_ENCONTRADOS],
          },
          {
            title: 'Relatório de análise de risco',
            to: SALES_REPORT_RISK_ANALYSIS_PAGE_URL,
            hasBullet: true,
            description: 'Vendas de autoatendimento com pontuação de risco',
            permissions: [RESOURCES.ANALISE_DE_RISCO],
          },
          {
            title: 'Conversão do PDV',
            to: SALES_REPORT_POINT_OF_SALE_CONVERSION_PAGE_URL,
            hasBullet: true,
            description: 'Vendas iniciadas, concluídas e não concluídas por ponto, adquirente, forma e horário',
            permissions: [RESOURCES.RELATORIOS_VENDAS_CONVERSAO_PDV],
          },
          {
            title: 'Notas Fiscais',
            to: '/relatorios/vendas/nota-fiscal',
            description: 'NFC-e emitidas por produto, operação, cliente e ao longo do tempo',
            icon: 'document',
            children: [
              {
                title: 'Consulta Produto',
                to: SALES_REPORT_INVOICE_PRODUCT_QUERY_PAGE_URL,
                description: 'Produtos das NFC-e emitidas, com quantidade, preço e status da nota',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_VENDAS_NOTA_FISCAL_CONSULTA_PRODUTO],
              },
              {
                title: 'Operação',
                to: SALES_REPORT_INVOICE_OPERATION_PAGE_URL,
                description: 'Quantidade e valor das NFC-e emitidas por operação (CFOP)',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_VENDAS_NOTA_FISCAL_OPERACAO],
              },
              {
                title: 'Cliente',
                to: SALES_REPORT_INVOICE_CLIENT_PAGE_URL,
                description: 'Quantidade e valor das NFC-e emitidas para cada cliente',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_VENDAS_NOTA_FISCAL_CLIENTE],
              },
              {
                title: 'Evolução',
                to: SALES_REPORT_INVOICE_EVOLUTION_PAGE_URL,
                description: 'NFC-e emitidas e canceladas por dia ou por mês',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_VENDAS_NOTA_FISCAL_EVOLUCAO],
              },
            ],
          },
        ],
      },

      // Relatórios de Suprimentos
      {
        title: 'Suprimentos',
        to: SUPPLY_REPORT_PAGE_URL,
        description: 'Estoque, compras, perdas e notas fiscais de entrada',
        icon: 'parcel',
        children: [
          {
            title: 'Perdas por produtos',
            to: SUPPLY_REPORT_PRODUCT_LOSS_PAGE_URL,
            hasBullet: true,
            description: 'Produtos com maiores perdas no período',
            permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_PERDA_PRODUTO],
          },
          {
            title: 'Quebra e acuracidade por PDV',
            to: SUPPLY_REPORT_SHRINKAGE_PAGE_URL,
            hasBullet: true,
            description:
              'Quanto cada ponto de venda perdeu no mês, por motivo, e quanto a contagem bateu com o estoque',
            permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_QUEBRA_ACURACIDADE],
          },
          {
            title: 'Ordem de Compra',
            to: SUPPLY_REPORT_PURCHASE_ORDER_PAGE_URL,
            hasBullet: true,
            description: 'Ordens de compra emitidas, pendentes e recebidas',
            permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_ORDEM_DE_COMPRA],
          },
          {
            title: 'Estoque',
            to: '/relatorios/suprimentos/estoque',
            description: 'Saldo, movimentação, produtos parados e custo do que foi vendido',
            icon: 'package',
            children: [
              {
                title: 'Entrada e Saída',
                to: SUPPLY_REPORT_STOCK_ENTRY_EXIT_PAGE_URL,
                description: 'Entradas e saídas de estoque por produto no período',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_ESTOQUE_ENTRADA_SAIDA],
              },
              {
                title: 'Saldo',
                to: SUPPLY_REPORT_STOCK_BALANCE_PAGE_URL,
                description: 'Quanto há de cada produto em estoque agora e quanto ele vale a custo e a venda',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_ESTOQUE_SALDO],
              },
              {
                title: 'Sem Circulação',
                to: SUPPLY_REPORT_STOCK_NO_CIRCULATION_PAGE_URL,
                description: 'Produtos parados, sem nenhuma movimentação no período',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_ESTOQUE_SEM_CIRCULACAO],
              },
              {
                title: 'Custo da Mercadoria Vendida',
                to: SUPPLY_REPORT_STOCK_COST_OF_GOODS_SOLD_PAGE_URL,
                hasBullet: true,
                description: 'Faturamento, custo das mercadorias vendidas e lucro bruto no período',
                permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_ESTOQUE_CUSTO_MERCADORIA_VENDIDA],
              },
            ],
          },
          {
            title: 'Notas de Entrada',
            to: '/relatorios/suprimentos/nota-de-entrada',
            description: 'NF-e de entrada por operação, fornecedor, produto e ao longo do tempo',
            icon: 'document',
            children: [
              {
                title: 'Operação',
                to: SUPPLY_REPORT_ENTRY_NOTE_OPERATION_PAGE_URL,
                description: 'NF-e de entrada agrupadas por operação fiscal (CFOP)',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_NOTA_ENTRADA_OPERACAO],
              },
              {
                title: 'Fornecedor',
                to: SUPPLY_REPORT_ENTRY_NOTE_SUPPLIER_PAGE_URL,
                description: 'NF-e de entrada agrupadas por fornecedor',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_NOTA_ENTRADA_FORNECEDOR],
              },
              {
                title: 'Produto',
                to: SUPPLY_REPORT_ENTRY_NOTE_PRODUCT_PAGE_URL,
                description: 'Produtos recebidos em NF-e de entrada, com custo médio',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_NOTA_ENTRADA_PRODUTO],
              },
              {
                title: 'Evolução',
                to: SUPPLY_REPORT_ENTRY_NOTE_EVOLUTION_PAGE_URL,
                description: 'Volume de NF-e de entrada ao longo do tempo',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_NOTA_ENTRADA_EVOLUCAO],
              },
              {
                title: 'Preço de compra por fornecedor',
                to: SUPPLY_REPORT_ENTRY_NOTE_PRODUCT_SUPPLIER_PAGE_URL,
                description: 'Custo de compra de cada produto comparado entre os fornecedores',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_SUPRIMENTOS_NOTA_ENTRADA_PRODUTO_FORNECEDOR],
              },
            ],
          },
        ],
      },

      // Relatórios de Finanças
      {
        title: 'Finanças',
        to: FINANCIAL_REPORT_PAGE_URL,
        description: 'Resultado, fluxo de caixa e contas a pagar e a receber',
        icon: 'dollar',
        children: [
          {
            title: 'Relatórios Gerais',
            to: '/relatorios/financas/geral',
            description: 'Saldo das contas, resultado do período e fluxo de caixa',
            icon: 'chart-simple',
            children: [
              {
                title: 'Saldos por conta financeira',
                to: FINANCIAL_REPORT_BALANCE_SHEET_PAGE_URL,
                description: 'Saldo anterior, entradas, saídas, ajustes e saldo final de cada conta',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_FINANCEIRO_BALANCETE],
              },
              {
                title: 'DRE',
                to: FINANCIAL_REPORT_DRE_PAGE_URL,
                description: 'Receitas, custos e despesas do período e o lucro apurado',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_FINANCEIRO_DRE],
              },
              {
                title: 'Resultado por PDV',
                to: FINANCIAL_REPORT_POINT_OF_SALE_RESULT_PAGE_URL,
                description: 'Margem de contribuição de cada ponto de venda, com o total igual ao da DRE',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_FINANCEIRO_RESULTADO_PDV],
              },
              {
                title: 'Fluxo de Caixa',
                to: FINANCIAL_REPORT_CASH_FLOW_PAGE_URL,
                description: 'Entradas e saídas no período, com saldo acumulado por data',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_FINANCEIRO_FLUXO_CAIXA],
              },
            ],
          },
          {
            title: 'Caixa',
            to: '/relatorios/financas/caixa',
            description: 'Lançamentos financeiros agrupados por categoria ou por cliente',
            icon: 'wallet',
            children: [
              {
                title: 'Categoria',
                to: FINANCIAL_REPORT_CASH_CATEGORY_PAGE_URL,
                description: 'De onde vem e para onde vai o dinheiro, por categoria',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_FINANCEIRO_CAIXA_CATEGORIA],
              },
              {
                title: 'Cliente',
                to: FINANCIAL_REPORT_CASH_CLIENT_PAGE_URL,
                description: 'Lançamentos financeiros agrupados por cliente ou fornecedor',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_FINANCEIRO_CAIXA_CLIENTE],
              },
            ],
          },
          {
            title: 'Contas',
            to: '/relatorios/financas/conta',
            description: 'Contas a pagar, a receber e pagamentos recebidos',
            icon: 'bill',
            children: [
              {
                title: 'A Pagar',
                to: FINANCIAL_REPORT_ACCOUNTS_PAYABLE_PAGE_URL,
                description: 'Contas a pagar por vencimento, status e fornecedor',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_FINANCEIRO_CONTAS_A_PAGAR],
              },
              {
                title: 'A Receber',
                to: FINANCIAL_REPORT_ACCOUNTS_RECEIVABLE_PAGE_URL,
                description: 'Contas a receber por vencimento, status e cliente',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_FINANCEIRO_CONTAS_A_RECEBER],
              },
              {
                title: 'Recebimento',
                to: FINANCIAL_REPORT_RECEIPTS_PAGE_URL,
                description: 'Pagamentos recebidos no período, por forma de pagamento',
                hasBullet: true,
                permissions: [RESOURCES.RELATORIOS_FINANCEIRO_RECEBIMENTO],
              },
            ],
          },
        ],
      },
    ],
  },

  // Finanças
  {
    title: 'Finanças',
    to: '/financas',
    icon: 'dollar',
    fontIcon: '',
    children: [
      {
        title: 'Competências',
        to: COMPETENCE_PAGE_URL,
        hasBullet: true,
        description: 'Competências financeiras',
        permissions: [RESOURCES.FINANCEIRO_PLANO_DE_CONTAS],
      },
      {
        title: 'Caixas e bancos',
        to: CASH_BANK_PAGE_URL,
        hasBullet: true,
        description: 'Caixa e bancos',
        permissions: [RESOURCES.FINANCEIRO_FLUXO_DE_CAIXA],
      },
      {
        title: 'Contas a pagar',
        to: ACCOUNTS_PAYABLE_PAGE_URL,
        hasBullet: true,
        description: 'Contas a pagar',
        permissions: [RESOURCES.FINANCEIRO_CONTAS_A_PAGAR],
      },
      {
        title: 'Contas a receber',
        to: ACCOUNTS_RECEIVABLE_PAGE_URL,
        hasBullet: true,
        description: 'Contas a receber',
        permissions: [RESOURCES.FINANCEIRO_CONTAS_A_RECEBER],
      },
      {
        title: 'Repasse a condomínio',
        to: COMMUNITY_SETTLEMENT_PAGE_URL,
        hasBullet: true,
        description: 'Repasse mensal do que se deve ao condomínio',
        permissions: [RESOURCES.FINANCEIRO_REPASSE_COMUNIDADES],
      },
    ],
  },

  // Espaço Contador
  {
    title: 'Espaço Contador',
    to: ACCOUNTANT_SPACE_PAGE_URL,
    icon: 'calculator',
    fontIcon: '',
    children: [
      {
        title: 'Dashboard',
        to: ACCOUNTANT_DASHBOARD_PAGE_URL,
        hasBullet: true,
        description: 'Dashboard do contador',
        permissions: [RESOURCES.ESPACO_CONTADOR_DASHBOARD],
      },
      {
        title: 'Gerenciar Acessos',
        to: ACCOUNTANT_ACCESS_MANAGEMENT_PAGE_URL,
        hasBullet: true,
        description: 'Gerenciar acessos do contador',
        permissions: [RESOURCES.ESPACO_CONTADOR_ACESSOS],
      },
      {
        title: 'Gerenciar convites',
        to: ACCOUNTANT_INVITE_MANAGEMENT_PAGE_URL,
        hasBullet: true,
        description: 'Gerenciar convites do contador',
        permissions: [RESOURCES.GERENCIAR_CONVITES],
      },
    ],
  },

  // Espaço Gestor
  {
    title: 'Espaço Gestor',
    to: MANAGER_SPACE_PAGE_URL,
    icon: 'people',
    fontIcon: '',
    children: [
      {
        title: 'Dashboard',
        to: MANAGER_DASHBOARD_PAGE_URL,
        hasBullet: true,
        description: 'Dashboard do gestor',
        permissions: [RESOURCES.ESPACO_GESTOR_DASHBOARD],
      },
    ],
  },
];

// Bottom menu items (non-collapsible, always visible)
const supportMenuItem: MenuItemConfig = {
  title: 'Suporte',
  to: SUPPORT_PAGE_URL,
  icon: 'question',
  description: 'Central de suporte',
  // Highlight style — pick one: 'tinted' | 'accent' | 'pulse' | 'badge' | 'count'
  highlight: { variant: 'tinted' },
  // For a badge instead, use: highlight: { variant: 'badge', label: 'Ajuda' },
};

export const systemUserBottomMenuConfig: MenuItemConfig[] = [
  {
    title: 'Gerenciador de Arquivos',
    to: '/arquivos',
    icon: 'folder',
    fontIcon: '',
    permissions: [RESOURCES.GERENCIAR_ARQUIVOS],
  },
  ...(TRAININGS_MOCK_ENABLED
    ? [
        {
          title: 'Treinamentos',
          to: TRAININGS_PAGE_URL,
          icon: 'teacher',
          description: 'Cursos da rede para a loja',
          badgeCountKey: MenuBadgeCountKey.NEW_TRAININGS,
        },
      ]
    : []),
  {
    title: 'Preferências',
    to: PREFERENCES_PAGE_URL,
    icon: 'setting-4',
    description: 'Configurações do sistema',
    permissions: systemPreferences.flatMap((area) =>
      area.sections.flatMap((section) => section.options.flatMap((option) => option.permissions ?? [])),
    ),
  },
  ...(TICKETS_MOCK_ENABLED
    ? [
        {
          title: 'Chamados',
          to: TICKETS_PAGE_URL,
          icon: 'support-24',
          description: 'Chamados para a matriz',
          badgeCountKey: MenuBadgeCountKey.WAITING_TICKETS,
        },
      ]
    : []),
  ...(TICKETS_MOCK_ENABLED ? [] : [supportMenuItem]),
];

/** Folha: basta uma das permissões (ou nenhuma exigida). Grupo: visível se algum descendente for. */
export const canAccessMenuItem = (item: MenuItemConfig, ability: AppAbility): boolean => {
  if (item.children?.length) return item.children.some((child) => canAccessMenuItem(child, ability));
  return (
    !item.permissions?.length || item.permissions.some((permission) => ability.can(AdminAction.CONSULTAR, permission))
  );
};

/**
 * Flatten menu items for search functionality
 * This function recursively flattens nested menu items and builds breadcrumb paths
 */
export const flattenMenuItems = (
  items: MenuItemConfig[],
  parentPath: string = '',
): Array<MenuItemConfig & { breadcrumb: string }> => {
  const flattened: Array<MenuItemConfig & { breadcrumb: string }> = [];

  items.forEach((item) => {
    const breadcrumb = parentPath ? `${parentPath} > ${item.title}` : item.title;

    // Add current item if its final does not have children or if it has a valid 'to' path
    if ((!item.children || item.children.length === 0) && item.to && item.to !== '') {
      flattened.push({ ...item, breadcrumb });
    }

    // Recursively add children
    if (item.children && item.children.length > 0) {
      flattened.push(...flattenMenuItems(item.children, breadcrumb));
    }
  });

  return flattened;
};
